import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { DollarSign, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useRole } from '@/hooks/useRole';
import { useRefetchOnFinanceChange } from '@/hooks/useWork';
import { supabase } from '@/integrations/supabase/client';
import type { FinanceEntryPrefill } from '@/pages/FinanceEntryFormPage';
import { todayIsoDate } from '@/lib/financeStatus';
import type { Quote } from '@/types/database';

const dismissKey = (quoteId: string) => `orcamigo_fin_prompt_dismissed_${quoteId}`;

interface Props {
  quote: Pick<Quote, 'id' | 'total' | 'client_id' | 'title' | 'status' | 'sequence_id'>;
}

/**
 * When a quote is approved/completed, invite the user to register the money as
 * a finance entry. We never create it automatically — the user decides when the
 * cash actually arrived. Hidden once an entry already links to this quote, once
 * dismissed, or when the active-company role cannot access finance.
 */
export function QuoteFinancePrompt({ quote }: Props) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { canSeeFinance } = useRole();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey(quote.id)) === '1';
    } catch {
      return false;
    }
  });

  const eligibleStatus = quote.status === 'approved' || quote.status === 'completed';
  const isRealQuote = !String(quote.id).startsWith('offline_');

  const { data: alreadyLinked, refetch: refetchLinked } = useQuery({
    queryKey: ['finance-quote-linked', quote.id, activeCompany?.id],
    enabled: Boolean(user?.id && activeCompany?.id) && eligibleStatus && isRealQuote && canSeeFinance && !dismissed,
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('finance_entries')
        .select('id', { count: 'exact', head: true })
        .eq('quote_id', quote.id)
        .eq('company_id', activeCompany!.id);
      if (error) throw error;
      return (count ?? 0) > 0;
    },
  });

  // Sem isto o convite CONTINUA na tela depois que o usuário registra o
  // recebimento por ele mesmo: a criação invalida a query de lançamentos, não
  // esta contagem (`staleTime` 60s e nada a dispara). Medido em device — o
  // convite ficava pedindo o que acabara de ser feito. Ver #80.
  useRefetchOnFinanceChange(() => void refetchLinked());

  if (!canSeeFinance || !eligibleStatus || dismissed || alreadyLinked) {
    return null;
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(quote.id), '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const prefill: FinanceEntryPrefill = {
    kind: 'income',
    amount: Number(quote.total ?? 0),
    client_id: quote.client_id ?? null,
    quote_id: quote.id,
    description: `Recebimento — ${quote.title || (quote.sequence_id ? `Orçamento #${quote.sequence_id}` : 'Orçamento')}`,
    due_date: todayIsoDate(),
  };

  return (
    <>
      <Card className="border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/20">
        <CardContent className="flex items-center gap-3 p-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/40">
            <DollarSign className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Registrar recebimento no financeiro?</p>
            <p className="text-xs text-muted-foreground">
              Lance esta venda no fluxo de caixa quando o dinheiro entrar.
            </p>
          </div>
          <Button3D size="sm" onClick={() => navigate('/financeiro/novo', { state: { prefill, defaultKind: 'income' } })}>
            Registrar
          </Button3D>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleDismiss}>
            <X className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

    </>
  );
}
